import React, { useCallback, useState } from "react";
import {
  DndContext,
  DragEndEvent,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { GripVertical, X, Plus, Video, Mic } from "lucide-react";
import EntityPickerModal from "@/components/EntityPickerModal/EntityPickerModal";
import { getSeriesEligibleResources } from "@/modules/hub/features/ResourceManagement/api/getSeriesEligibleResources/getSeriesEligibleResourcesApi";

export interface SeriesEntryEntity {
  id: number;
  name: string;
  thumbnailUrl?: string | null;
  primaryIcon?: string | null;
  resourceType?: string | null;
  createdAt?: string;
  sortOrder?: number;
}

interface SortableSeriesEntriesProps {
  value: SeriesEntryEntity[];
  onChange: (entries: SeriesEntryEntity[]) => void;
}

interface SortableSeriesEntryProps {
  entry: SeriesEntryEntity;
  onRemove: () => void;
}

function SortableSeriesEntry({ entry, onRemove }: SortableSeriesEntryProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: entry.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getResourceIcon = () => {
    if (entry.resourceType === "Video") return <Video className="h-4 w-4" />;
    if (entry.resourceType === "Podcast") return <Mic className="h-4 w-4" />;
    return null;
  };

  console.log("josh entry", entry);

  return (
    <div ref={setNodeRef} style={style}>
      <Card className="p-3">
        <div className="flex items-center gap-3">
          {/* Drag Handle */}
          <button
            className="cursor-grab hover:cursor-grabbing text-gray-400 hover:text-gray-600"
            type="button"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-5 w-5" />
          </button>

          {/* Thumbnail or Icon */}
          <div className="flex-shrink-0 w-12 h-12 rounded overflow-hidden bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
            {entry.thumbnailUrl ? (
              <img
                alt={entry.name}
                className="w-full h-full object-cover"
                src={entry.thumbnailUrl}
              />
            ) : entry.primaryIcon ? (
              <span
                className="text-gray-500 scale-75"
                dangerouslySetInnerHTML={{ __html: entry.primaryIcon }}
              />
            ) : (
              <div className="text-gray-400">{getResourceIcon()}</div>
            )}
          </div>

          {/* Title and Type */}
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm truncate">{entry.name}</p>
            {entry.resourceType && (
              <Badge className="text-xs mt-1" variant="secondary">
                {entry.resourceType}
              </Badge>
            )}
          </div>

          {/* Remove Button */}
          <Button
            className="text-gray-400 hover:text-red-600"
            onClick={onRemove}
            size="sm"
            type="button"
            variant="ghost"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </Card>
    </div>
  );
}

export function SortableSeriesEntries({
  value,
  onChange,
}: SortableSeriesEntriesProps) {
  const [modalOpen, setModalOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = value.findIndex((entry) => entry.id === active.id);
      const newIndex = value.findIndex((entry) => entry.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newEntries = arrayMove(value, oldIndex, newIndex).map(
          (entry, index) => ({ ...entry, sortOrder: index }),
        );
        onChange(newEntries);
      }
    },
    [value, onChange],
  );

  const handleRemove = useCallback(
    (id: number) => {
      onChange(value.filter((entry) => entry.id !== id));
    },
    [value, onChange],
  );

  const handleConfirm = useCallback(
    (newSelection: SeriesEntryEntity[]) => {
      const updatedEntries = newSelection.map((entry, index) => ({
        ...entry,
        sortOrder: index,
      }));
      onChange(updatedEntries);
      setModalOpen(false);
    },
    [onChange],
  );

  return (
    <div className="space-y-4">
      {/* Sortable List */}
      {value.length > 0 && (
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          sensors={sensors}
        >
          <SortableContext
            items={value.map((entry) => entry.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2">
              {value.map((entry) => (
                <SortableSeriesEntry
                  entry={entry}
                  key={entry.id}
                  onRemove={() => handleRemove(entry.id)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {value.length === 0 && (
        <div className="text-center py-8 text-gray-500 border border-dashed rounded-lg">
          No videos or podcasts added to this series yet.
        </div>
      )}

      {/* Add Button */}
      <Button
        className="w-full"
        onClick={() => setModalOpen(true)}
        type="button"
        variant="outline"
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Videos/Podcasts to Series
      </Button>

      {/* Entity Picker Modal */}
      <EntityPickerModal<SeriesEntryEntity>
        entityNamePlural="Videos/Podcasts"
        entityNameSingular="Resource"
        fetchEntities={async ({ searchTerm, page, pageSize }) => {
          const response = await getSeriesEligibleResources({
            searchTerm,
            page,
            pageSize,
            sortBy: "title",
            sortOrder: "ASC",
          });
          const totalCount = response.meta?.totalCount ?? response.data.length;

          return {
            data: response.data.map((r) => ({
              id: r.id,
              name: r.title,
              thumbnailUrl: r.thumbnailUrl,
              primaryIcon: r.primaryIcon,
              resourceType: r.resourceType,
              createdAt: r.createdAt,
            })),
            totalCount,
          };
        }}
        initialSelectedEntities={value}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleConfirm}
        renderEntityRow={(ent, isSelected, toggle) => {
          return (
            <div
              className={`
                flex items-center gap-3 p-3 border rounded-lg cursor-pointer
                transition-colors hover:bg-gray-50 dark:hover:bg-gray-800
                ${isSelected ? "bg-blue-50 dark:bg-blue-900/20 border-blue-500" : ""}
              `}
              onClick={() => toggle(ent)}
            >
              <input
                checked={isSelected}
                className="cursor-pointer"
                onChange={() => {}}
                type="checkbox"
              />
              <div className="flex-shrink-0 w-10 h-10 rounded overflow-hidden bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                {ent.thumbnailUrl ? (
                  <img
                    alt={ent.name}
                    className="w-full h-full object-cover"
                    src={ent.thumbnailUrl}
                  />
                ) : ent.primaryIcon ? (
                  <span
                    className="text-gray-500 scale-75"
                    dangerouslySetInnerHTML={{ __html: ent.primaryIcon }}
                  />
                ) : null}
              </div>
              <div className="flex-1">
                <p className="font-medium text-sm">{ent.name}</p>
                {ent.resourceType && (
                  <Badge className="text-xs mt-1" variant="secondary">
                    {ent.resourceType}
                  </Badge>
                )}
              </div>
            </div>
          );
        }}
      />
    </div>
  );
}
